import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { User, Mail, Phone, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useCustomerAuth, formatApiError } from '../context/CustomerAuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerAuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register } = useCustomerAuth();
  const [mode, setMode] = useState('login'); // 'login' | 'register' | 'forgot'
  const [submitting, setSubmitting] = useState(false);
  const [sentTo, setSentTo] = useState('');
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });

  const redirectTo = location.state?.from || '/my-account';
  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (mode === 'forgot') {
        const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: form.email }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(formatApiError(data.detail));
        setSentTo(form.email);
        toast.success('Reset link sent (if the account exists)');
        return;
      }
      if (mode === 'register') {
        if (!form.name || !form.email || !form.phone || !form.password) {
          toast.error('Please fill in all fields');
          setSubmitting(false);
          return;
        }
        await register(form);
        toast.success('Account created! Welcome aboard.');
      } else {
        await login(form.email, form.password);
        toast.success('Welcome back!');
      }
      navigate(redirectTo);
    } catch (err) {
      toast.error(err.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  const titles = { login: 'Welcome Back', register: 'Create Your Account', forgot: 'Reset Your Password' };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-16 px-4">
      <Card className="w-full max-w-md shadow-xl" data-testid="customer-auth-card">
        <CardHeader className="text-center">
          <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <User className="w-7 h-7 text-amber-600" />
          </div>
          <CardTitle className="text-2xl">{titles[mode]}</CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            {mode === 'login' && 'Log in to view your inquiries and itineraries'}
            {mode === 'register' && 'Sign up to track your inquiries and receive itineraries'}
            {mode === 'forgot' && "Enter your email and we'll send you a reset link"}
          </p>
        </CardHeader>
        <CardContent>
          {mode === 'forgot' && sentTo ? (
            <div className="text-center space-y-4" data-testid="forgot-sent">
              <Mail className="w-12 h-12 mx-auto text-amber-500" />
              <p className="text-gray-700">
                If an account exists for <strong>{sentTo}</strong>, a password reset link has been sent.
                Please check your inbox (and spam). The link expires in 1 hour.
              </p>
              <Button variant="outline" className="w-full" onClick={() => { setMode('login'); setSentTo(''); }}>
                Back to Login
              </Button>
            </div>
          ) : (
            <>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="customer-auth-form">
                {mode === 'register' && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Full Name</label>
                    <div className="relative">
                      <User className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                      <Input data-testid="auth-name-input" className="pl-9" value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder="Your name" />
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <div className="relative">
                    <Mail className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                    <Input data-testid="auth-email-input" type="email" className="pl-9" value={form.email} onChange={(e) => setField('email', e.target.value)} placeholder="you@example.com" />
                  </div>
                </div>
                {mode === 'register' && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Phone Number</label>
                    <div className="relative">
                      <Phone className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                      <Input data-testid="auth-phone-input" className="pl-9" value={form.phone} onChange={(e) => setField('phone', e.target.value)} placeholder="+91 XXXXX XXXXX" />
                    </div>
                  </div>
                )}
                {mode !== 'forgot' && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium">Password</label>
                      {mode === 'login' && (
                        <button type="button" data-testid="forgot-password-link" onClick={() => { setMode('forgot'); setSentTo(''); }} className="text-xs text-amber-600 hover:text-amber-700 font-medium">
                          Forgot password?
                        </button>
                      )}
                    </div>
                    <div className="relative">
                      <Lock className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                      <Input data-testid="auth-password-input" type="password" className="pl-9" value={form.password} onChange={(e) => setField('password', e.target.value)} placeholder="Your password" />
                    </div>
                  </div>
                )}
                <Button type="submit" disabled={submitting} data-testid="auth-submit-btn" className="w-full bg-amber-600 hover:bg-amber-700 text-white">
                  {submitting ? 'Please wait...' : mode === 'login' ? 'Log In' : mode === 'register' ? 'Create Account' : 'Send Reset Link'}
                </Button>
              </form>

              <div className="text-center mt-4 space-y-2">
                {mode === 'forgot' ? (
                  <button type="button" onClick={() => setMode('login')} className="text-amber-600 hover:text-amber-700 font-medium text-sm">
                    Back to Login
                  </button>
                ) : (
                  <button
                    type="button"
                    data-testid="auth-toggle-mode"
                    onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
                    className="text-amber-600 hover:text-amber-700 font-medium text-sm"
                  >
                    {mode === 'login' ? "Don't have an account? Sign up" : 'Already have an account? Log in'}
                  </button>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomerAuthPage;
